import { Controller } from "@hotwired/stimulus"

// Type-to-search replacement for a product <select>. A catalog of a few thousand
// items makes an option list unusable — and unusable once per line item — so
// this asks the server for the top matches instead and never ships the rest.
//
// The visible input is the form control the browser validates; the hidden field
// carries product_id. They are kept in lockstep: leaving the box reverts the
// text to whatever is actually selected, so text present always means a real
// product is chosen, and `required` on the input means what it says.
export default class extends Controller {
  static targets = ["input", "hidden", "results"]
  static values = { url: String, scopeSelector: String }

  static DEBOUNCE_MS = 200

  connect() {
    this.label = this.inputTarget.value
    this.options = []
    this.activeIndex = -1
    this.timer = null
    this.requestId = 0

    // The results list is fixed rather than absolute: on phones .qb-grid-table
    // scrolls inside its own box, which would clip a dropdown positioned
    // against the cell.
    this.reposition = () => { if (!this.resultsTarget.hidden) this.placeResults() }
    window.addEventListener("scroll", this.reposition, true)
    window.addEventListener("resize", this.reposition)

    // A purchase order limits products to what the chosen supplier stocks, so a
    // change there can invalidate a pick made under the previous one.
    if (this.hasScopeSelectorValue) {
      this.scopeElement = document.querySelector(this.scopeSelectorValue)
      this.onScopeChange = () => this.clear()
      this.scopeElement?.addEventListener("change", this.onScopeChange)
    }
  }

  disconnect() {
    clearTimeout(this.timer)
    window.removeEventListener("scroll", this.reposition, true)
    window.removeEventListener("resize", this.reposition)
    this.scopeElement?.removeEventListener("change", this.onScopeChange)
  }

  // --- searching ---------------------------------------------------------

  search() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.fetchResults(), this.constructor.DEBOUNCE_MS)
  }

  async fetchResults() {
    const params = new URLSearchParams({ q: this.inputTarget.value.trim() })
    const scope = this.scopeElement?.value
    if (scope) params.set("supplier_id", scope)

    // Responses can land out of order; only the newest one may paint.
    const id = ++this.requestId

    try {
      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) throw new Error(response.statusText)

      const payload = await response.json()
      if (id !== this.requestId) return

      this.render(payload.results, payload.truncated)
    } catch {
      if (id === this.requestId) this.render([], false, "Could not reach the catalog.")
    }
  }

  render(options, truncated, error) {
    this.options = options
    this.activeIndex = -1
    this.resultsTarget.replaceChildren()

    if (error) {
      this.resultsTarget.appendChild(this.notice(error))
    } else if (options.length === 0) {
      this.resultsTarget.appendChild(this.notice("No matching products."))
    } else {
      options.forEach((option, index) => {
        this.resultsTarget.appendChild(this.buildOption(option, index))
      })
      if (truncated) {
        this.resultsTarget.appendChild(this.notice("More matches — keep typing to narrow."))
      }
    }

    this.open()
  }

  buildOption(option, index) {
    const item = document.createElement("li")
    item.className = "product-picker-option"
    item.textContent = option.label
    item.role = "option"
    item.dataset.index = index
    // mousedown, not click: blur fires first and would close the list.
    item.addEventListener("mousedown", (event) => {
      event.preventDefault()
      this.choose(index)
    })
    return item
  }

  notice(text) {
    const item = document.createElement("li")
    item.className = "product-picker-note"
    item.textContent = text
    return item
  }

  // --- selection ---------------------------------------------------------

  choose(index) {
    const option = this.options[index]
    if (!option) return

    this.label = option.label
    this.inputTarget.value = option.label
    this.hiddenTarget.value = option.id
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
  }

  clear() {
    this.label = ""
    this.inputTarget.value = ""
    this.hiddenTarget.value = ""
    this.close()
  }

  navigate(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.resultsTarget.hidden ? this.search() : this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Enter":
        // Only swallow Enter while choosing, so it still submits the form.
        if (!this.resultsTarget.hidden && this.activeIndex >= 0) {
          event.preventDefault()
          this.choose(this.activeIndex)
        }
        break
      case "Escape":
        this.close()
        break
    }
  }

  move(step) {
    if (this.options.length === 0) return

    const count = this.options.length
    this.activeIndex = (this.activeIndex + step + count) % count
    this.highlight()
  }

  highlight() {
    this.resultsTarget.querySelectorAll(".product-picker-option").forEach((item, index) => {
      const active = index === this.activeIndex
      item.classList.toggle("active", active)
      item.setAttribute("aria-selected", active)
      if (active) item.scrollIntoView({ block: "nearest" })
    })
  }

  // --- open/close --------------------------------------------------------

  open() {
    this.resultsTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
    this.placeResults()
  }

  close() {
    clearTimeout(this.timer)
    // Half-typed text with nothing chosen would leave the hidden field empty
    // while the box still looked filled in.
    this.inputTarget.value = this.label
    this.resultsTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.activeIndex = -1
  }

  placeResults() {
    const box = this.inputTarget.getBoundingClientRect()
    const style = this.resultsTarget.style
    style.left = `${box.left}px`
    style.width = `${box.width}px`

    // Flip above the field when the space below cannot hold the list.
    const below = window.innerHeight - box.bottom
    const height = this.resultsTarget.offsetHeight

    if (below < height && box.top > below) {
      style.top = `${box.top - height}px`
    } else {
      style.top = `${box.bottom}px`
    }
  }
}
